import * as React from 'react';

/**
 * Which editor a person uses for a screen: the stack builder or the classic one. Per person and per
 * browser, so switching back is a one-click fallback for anyone hitting a problem in the new editor,
 * without a deploy or a feature toggle. The stack builder is the default.
 */
export type EditorChoice = 'stack' | 'classic';
export type EditorScreen = 'sequence' | 'offerBlock';

const storageKey = (screen: EditorScreen) => `zuul.editor.${screen}`;
const listeners = new Set<() => void>();

function read(screen: EditorScreen): EditorChoice {
  try {
    return window.localStorage.getItem(storageKey(screen)) === 'classic' ? 'classic' : 'stack';
  } catch {
    return 'stack';
  }
}

export function setEditorChoice(screen: EditorScreen, choice: EditorChoice) {
  try {
    window.localStorage.setItem(storageKey(screen), choice);
  } catch {
    // Storage blocked (private mode, policy): the choice holds for this page only.
    memory.set(screen, choice);
  }
  listeners.forEach((listener) => listener());
}

const memory = new Map<EditorScreen, EditorChoice>();

export function useEditorChoice(screen: EditorScreen) {
  const subscribe = React.useCallback(
    (listener: () => void) => {
      listeners.add(listener);
      const onStorage = (event: StorageEvent) => event.key === storageKey(screen) && listener();
      window.addEventListener('storage', onStorage);
      return () => {
        listeners.delete(listener);
        window.removeEventListener('storage', onStorage);
      };
    },
    [screen]
  );
  const choice = React.useSyncExternalStore(subscribe, () => memory.get(screen) ?? read(screen));
  const set = React.useCallback((next: EditorChoice) => setEditorChoice(screen, next), [screen]);
  return [choice, set] as const;
}
